import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Types
type Lesson = {
  id: number;
  title: string;
  learning_objectives: string;
  prerequisites: string;
  sort_order: number;
  cover_image_url?: string;
};

type Stage = {
  id: number;
  title: string;
  sort_order: number;
  lessons?: Lesson[];
  isLoading?: boolean;
};

type CourseCache = {
  [courseId: number]: Stage[];
};

type CourseUIState = {
  // State
  currentCourseId: number | null; // Track current course to detect switches
  expandedStageIds: number[];
  selectedLessonId: number | null;
  courseCache: CourseCache;

  // Actions
  initCourse: (courseId: number) => void;
  toggleStage: (stageId: number) => void;
  setExpandedStages: (ids: number[]) => void;
  setSelectedLessonId: (id: number | null) => void;
  
  setCourseData: (courseId: number, stages: Stage[]) => void;
  updateStageLessons: (courseId: number, stageId: number, lessons: Lesson[]) => void;
  updateLesson: (courseId: number, lessonId: number, data: Partial<Lesson>) => void; // New helper
};

export const useCourseUIStore = create<CourseUIState>()(
  persist(
    (set, get) => ({
      currentCourseId: null,
      expandedStageIds: [],
      selectedLessonId: null,
      courseCache: {},

      // Initialize: Check if course changed, if so, reset selection
      initCourse: (courseId) => {
        const state = get();
        if (state.currentCourseId !== courseId) {
          set({
            currentCourseId: courseId,
            selectedLessonId: null,
            expandedStageIds: [] // Optional: reset expansion on new course
          });
        }
      },

      toggleStage: (stageId) => set((state) => {
        const isCurrentlyExpanded = state.expandedStageIds.includes(stageId);
        return {
          expandedStageIds: isCurrentlyExpanded ? [] : [stageId]
        };
      }),

      setExpandedStages: (ids) => set({ expandedStageIds: ids }),
      setSelectedLessonId: (id) => set({ selectedLessonId: id }),

      setCourseData: (courseId, stages) => set((state) => ({
        courseCache: { ...state.courseCache, [courseId]: stages }
      })),

      updateStageLessons: (courseId, stageId, lessons) => set((state) => {
        const currentStages = state.courseCache[courseId] || [];
        const newStages = currentStages.map(s => 
          s.id === stageId ? { ...s, lessons, isLoading: false } : s
        );
        return {
          courseCache: { ...state.courseCache, [courseId]: newStages }
        };
      }),

      // Helper to update a single lesson in cache (e.g. after image upload)
      updateLesson: (courseId, lessonId, data) => set((state) => {
        const currentStages = state.courseCache[courseId] || [];
        // Deep update
        const newStages = currentStages.map(stage => ({
          ...stage,
          lessons: stage.lessons?.map(l => l.id === lessonId ? { ...l, ...data } : l)
        }));
        return {
          courseCache: { ...state.courseCache, [courseId]: newStages }
        };
      }),
    }),
    {
      name: 'nexus-course-ui-storage', // key in localStorage
      storage: createJSONStorage(() => localStorage),
      // Only persist UI state, not the huge data cache if you want to save space
      // But for performance, persisting cache is also fine.
      partialize: (state) => ({ 
        expandedStageIds: state.expandedStageIds,
        selectedLessonId: state.selectedLessonId,
        currentCourseId: state.currentCourseId,
        // Uncomment next line if you want to persist data cache too (Faster load, but stale data risk)
        // courseCache: state.courseCache 
      }),
    }
  )
);
